import axios from 'axios';
import utils from 'utils/utils';
import loginController from '../controllers/loginController';
import listenForInteraction from '../utils/listenForInteraction';
import biometricReAuthenticateController from '../controllers/biometricReAuthenticateController';
import mainAPI from './MainAPI';
import {VERSION} from "../components/pages/loginHome/LoginHome";
import {isCordova} from "../utils/isCordova";

let axiosConfig = {
	headers: {
		'Content-Type': 'application/json',
		'Access-Control-Allow-Origin': '*'
	}
};

/*
 * Default parameters for handleError function.
 */
const defaults = {
	error: {response: {data: {}}, request: {data: {}}, config: {}},
	options: {type: 'Network Error'}
};

export const handleError = async ({error = defaults.error, options: {type} = defaults.options} = {}, reportError=true) => {
	const {response, response: {status, data: resData}, request: {data: reqData}, config: {url, method}} = error;
	switch (status) {
		case 403:
			console.log('User is not authorized for API');
			return {error};
		case 401:
			listenForInteraction.pause();
			if (await biometricReAuthenticateController.isEligible()) {
				biometricReAuthenticateController.show();
				return {error};
			}
			console.log('User Logged Out');
			loginController.show();
			return {error};
		case 404:
			return {error};
	}
	const message = `HTTP: ${status} URL: ${url} METHOD: ${method} RESPONSE: ${JSON.stringify(resData)} REQUEST: ${JSON.stringify(reqData)}`;
	if(reportError) {
		await mainAPI.error_log.create({type, message});
	}
	console.log('ERROR', error, status, response);
	return {error};
}

export const handleNetworkError = async (e) => {
	let response = e.response || {};
	let status = response?.status;
	if (status === 403) {
		return console.log('User is not authorized for API');
	} else if (status === 401) {
		listenForInteraction.pause();
		if (await biometricReAuthenticateController.isEligible()) {
			return biometricReAuthenticateController.show();
		}
		console.log('User Logged Out');
		return loginController.show();
	} else if (status === 404) {
		return;
		//do nothing
	}

	await mainAPI.error_log.create({
		type: 'Network Error',
		message:
			status +
			e?.config?.url +
			e?.config?.method +
			' ' + JSON.stringify(response?.data) +
			' ' + (e?.config?.data) +
			' ' + e
	});
	console.log('ERROR', e, status, response);
	// throw new Error(e);
	// requests.fireErrors(response);
};

let pre = window.location.origin;

if (isCordova()) {
	pre = 'https://api.logrx.com';
	if (localStorage.useSandbox) {
		pre = 'https://dev.logrx.com';
	}
}
pre = pre + '/api/v1/';

const CancelToken = axios.CancelToken;

let cancelRequest = (cancel) => {
	setTimeout(() => {
		cancel();
	}, 15000);
};

let serialize = (obj) => {
	const str = [];
	for (let p in obj)
		if (obj.hasOwnProperty(p)) {
			str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
		}
	return str.join('&');
};
function generateUUIDv4() {
	return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
		(c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
	);
}
const additionalHeaders = () => {
	let headers = {}
	headers['x-platform-type'] = isCordova() ? 'CORDOVA' : 'WEB';
	headers['x-platform'] = window?.cordova?.platformId
	headers['x-service-name'] = 'app';
	headers['x-app-version'] = VERSION;
	headers['x-region'] = localStorage.region;
	headers['x-auth-token'] = localStorage.token;
	headers['user-group-id'] = localStorage.user_group_id || ''; // renamed. remove in the future
	headers['x-user-group-id'] = localStorage.user_group_id || '';
	headers['x-trace-id'] = generateUUIDv4();
	headers.last_log_in = localStorage.last_log_in;
	headers['last-log-in'] = localStorage.last_log_in;
	return headers;
};



const retryRequest = async (requestFn, maxRetries = 3, baseDelay = 1000, url) => {
	for (let attempt = 0; attempt <= maxRetries; attempt++) {
		try {
			return await requestFn();
		} catch (error) {
			const status = error.response?.status;
			
			// Only retry on 5xx errors
			if ((!status || (status >= 500 && status < 600)) && attempt < maxRetries) {
				const delay = baseDelay * Math.pow(2, attempt); // Exponential backoff
				console.log(`Request failed with ${status}, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
				await new Promise(resolve => setTimeout(resolve, delay));
				continue;
			}
			
			// If it's not a 5xx error or we've exhausted retries, throw the error
			throw error;
		}
	}
};

const get = (url, params, optionalHeaders = {}, errorHandler, retries=3) => {
	let data = {
		...params,
		platformType: isCordova() ? 'CORDOVA' : ''
	};
	url += '?' + serialize(data);

	if (!localStorage.last_log_in) localStorage.last_log_in = Date.now();

	const makeRequest = () => axios
		.get(pre + url, {
			cancelToken: new CancelToken(function executor(c) {
				cancelRequest(c);
				// c()
			}),
			headers: {
				...additionalHeaders(),
				...optionalHeaders
			}
		})
		.then((res) => {
			if (res.data.data && res.data.metadata) {
				res.data.data = utils.server_to_client_obj(res.data.data, null, res.data.metadata);
			}
			return res.data.data;
		});

	return retryRequest(makeRequest, retries, null, url)
		.catch((err) => {
			if (errorHandler) {
				return errorHandler(err);
			} else {
				handleNetworkError(err).then();
				return;
			}
		});
};
const getNoAuth = (url, data) => {
	data = data || {};
	data.platformType = isCordova() ? 'CORDOVA' : ''
	url += '?' + serialize(data);
	let headers = additionalHeaders();
	delete headers['x-auth-token'];
	return axios
		.get(pre + url, {
			cancelToken: new CancelToken(function executor(c) {
				cancelRequest(c);
				// c()
			}, {
				headers: headers
			})
		})
		.then((res) => {
			if (res.data.data && res.data.metadata) {
				res.data.data = utils.server_to_client_obj(res.data.data, null, res.data.metadata);
			}
			return res.data.data;
		})
		.catch((err) => {
			handleNetworkError(err);
		});
};

const post = (url, body, config, errorHandler) => {
	let data = {...body, password_reset_guid: localStorage.password_reset_guid};

	if (!config) {
		config = axiosConfig;
	}
	if (!config.headers) {
		config.headers = {};
	}
	config.headers = {...config.headers, ...additionalHeaders()};

	url += '?platformType=' + (isCordova() ? 'CORDOVA' : '')
	return axios
		.post(pre + url, data, config)
		.then((res) => {
			if (res.data.data && res.data.metadata) {
				res.data.data = utils.server_to_client_obj(res.data.data, null, res.data.metadata);
			}
			return res.data.data;
		})
		.catch((err) => {
			if (errorHandler) {
				return errorHandler(err);
			} else {
				handleNetworkError(err);
			}
		});
};
const postReturnError = (url, body, config, reportError) => {
	return post(url, body, config, (error) => handleError({error},reportError))
}
const getReturnError = (url, body, config) => {
	return get(url, body, config, (error) => {
		return handleError({error})
	})
}
const _delete = (url, data, config) => {
	if (!config) {
		config = axiosConfig;
	}
	if (!config.headers) {
		config.headers = {};
	}
	config.headers = {...config.headers, ...additionalHeaders()};

	url += '?platformType=' + (isCordova() ? 'CORDOVA' : '')
	return axios
		.delete(pre + url, data, config)
		.then((res) => {
			return res.data.data;
		})
		.catch((err) => {
			handleNetworkError(err);
		});
}

export default () => {
	return {
		get, post, postReturnError, delete: _delete, getNoAuth,getReturnError, generateUUIDv4
	};
};
